// src/utils/chartRenderers.js
// Функции для рендеринга различных типов графиков

import {
  createFormatters,
  easeOutC,
  clamp,
  niceStep,
  buildPalette,
  dateFormatter,
  positionTooltip,
  createSVGElement,
  createSVGGroup,
  createSVGLine,
  createSVGText,
  pathCatmullRom,
} from "./chartUtils";

const { fmtCompact, fmtNum } = createFormatters();

export function makeHistogram(cardEl, valuesOrRows, chartRegistry, root) {
  let rows;
  if (
    Array.isArray(valuesOrRows) &&
    valuesOrRows.length &&
    typeof valuesOrRows[0] === "object"
  ) {
    rows = valuesOrRows
      .map(({ t, y }) => ({ t: new Date(t), v: +y }))
      .sort((a, b) => a.t - b.t);
  } else {
    const values = (valuesOrRows || []).map((v) => +v);
    const today = new Date();
    rows = values
      .map((v, i) => ({
        t: new Date(+today - (values.length - 1 - i) * 86400000),
        v,
      }))
      .sort((a, b) => a.t - b.t);
  }

  chartRegistry.set(cardEl.id, {
    type: "hist",
    data: rows.map((r) => ({ t: r.t.toISOString(), y: r.v })),
  });

  const chart = cardEl.querySelector(".chart");
  const svg = chart.querySelector("svg");
  const tooltip = chart.querySelector(".tooltip");

  const WIDTH = 960,
    HEIGHT = 460;
  const M = { top: 18, right: 24, bottom: 92, left: 64 };
  const innerW = WIDTH - M.left - M.right;
  const innerH = HEIGHT - M.top - M.bottom;

  svg.setAttribute("viewBox", `0 0 ${WIDTH} ${HEIGHT}`);
  svg.setAttribute("preserveAspectRatio", "xMidYMid meet");

  while (svg.firstChild) svg.removeChild(svg.firstChild);

  const g = createSVGGroup(svg, `translate(${M.left},${M.top})`, null, root);
  const gridG = createSVGGroup(g, null, null, root);
  const barsG = createSVGGroup(g, null, null, root);
  const axisXG = createSVGGroup(g, null, null, root);
  const axisYG = createSVGGroup(g, null, null, root);

  const dates = rows.map((r) => r.t);
  const maxV = Math.max(0, ...rows.map((d) => d.v));
  const yStep = niceStep(maxV, 6);
  const yMax = Math.ceil(maxV / yStep) * yStep || 1;

  const yScale = (val) => Math.round(innerH * (1 - val / yMax));

  const bandStep = innerW / Math.max(1, rows.length);
  const bandPad = Math.min(0.2, 6 / Math.max(1, rows.length));
  const barW = Math.min(
    Math.max(3, bandStep * (1 - bandPad)),
    Math.max(3, bandStep - 4)
  );

  for (let y = 0; y <= yMax + 1e-9; y += yStep) {
    const yy = yScale(y);
    createSVGLine(gridG, 0, yy, innerW, yy, "grid-line", root);
    const tick = createSVGGroup(axisYG, null, "tick", root);
    createSVGText(tick, fmtCompact(y), -10, yy + 4, { "text-anchor": "end" }, root);
  }

  const dateFmt = dateFormatter(dates);
  const maxXTicks = Math.min(16, Math.max(4, Math.floor(innerW / 36)));
  const stepIdx = Math.ceil(rows.length / maxXTicks);

  rows.forEach((d, i) => {
    const cx = (i + 0.5) * bandStep;
    if (i % stepIdx === 0 || i === rows.length - 1) {
      const tick = createSVGGroup(axisXG, null, "tick", root);
      const y = innerH + 8;
      createSVGText(
        tick,
        dateFmt(d.t),
        cx,
        y,
        {
          "text-anchor": "end",
          transform: `rotate(-90 ${cx} ${y})`,
        },
        root
      );
    }
  });
  createSVGLine(axisXG, 0, innerH, innerW, innerH, "axis-line", root);

  const { colors, hovers } = buildPalette(rows.length);

  const bars = rows.map((d, i) => {
    const cx = (i + 0.5) * bandStep;
    const x = cx - barW / 2;
    const y = yScale(d.v);
    const h = innerH - y;
    return {
      x,
      y,
      h,
      bw: barW,
      v: d.v,
      t: d.t,
      color: colors[i],
      hoverColor: hovers[i],
      cx,
    };
  });

  const tStart = performance.now(),
    duration = 900;
  function frame(now) {
    const k = Math.min(1, (now - tStart) / duration);
    const t = easeOutC(k);
    barsG.innerHTML = "";

    bars.forEach((b) => {
      const groupEl = createSVGGroup(barsG, null, "bar-group", root);

      const hNow = Math.max(0.5, b.h * t);
      const yNow = innerH - hNow;

      const rect = createSVGElement("rect");
      rect.setAttribute("x", b.x);
      rect.setAttribute("y", yNow);
      rect.setAttribute("width", b.bw);
      rect.setAttribute("height", hNow);
      rect.setAttribute("fill", b.color);
      rect.setAttribute("class", "bar");

      const lbl = createSVGElement("text");
      const baseY = Math.max(12, yNow - 6);
      lbl.setAttribute("x", b.cx);
      lbl.setAttribute("y", baseY);
      lbl.setAttribute("class", "bar-label");
      lbl.setAttribute("text-anchor", "middle");
      lbl.setAttribute("dominant-baseline", "middle");
      lbl.setAttribute("fill", "#2b3242");
      lbl.textContent = fmtCompact(b.v);

      const hit = createSVGElement("rect");
      hit.setAttribute("x", b.x - 3);
      hit.setAttribute("y", yNow - 8);
      hit.setAttribute("width", b.bw + 6);
      hit.setAttribute("height", hNow + 12);
      hit.setAttribute("fill", "rgba(0,0,0,0)");

      groupEl.appendChild(rect);
      groupEl.appendChild(lbl);
      groupEl.appendChild(hit);

      const onEnter = (evt) => {
        rect.setAttribute("fill", b.hoverColor);
        lbl.classList.add("hover");
        tooltip.innerHTML = `<strong>${new Intl.DateTimeFormat("uz-UZ", {
          dateStyle: "medium",
        }).format(b.t)}</strong><br>${fmtCompact(b.v)}`;
        tooltip.classList.add("show");
        tooltip.setAttribute("aria-hidden", "false");
        positionTooltip(tooltip, chart, evt.clientX, evt.clientY, "right", 12);
      };
      const onMove = (evt) =>
        positionTooltip(tooltip, chart, evt.clientX, evt.clientY, "right", 12);
      const onLeave = () => {
        rect.setAttribute("fill", b.color);
        lbl.classList.remove("hover");
        tooltip.classList.remove("show");
        tooltip.setAttribute("aria-hidden", "true");
      };

      hit.addEventListener("pointerenter", onEnter);
      hit.addEventListener("pointermove", onMove);
      hit.addEventListener("pointerleave", onLeave);
      hit.addEventListener("touchstart", onEnter, { passive: true });
      hit.addEventListener("touchmove", onMove, { passive: true });
      hit.addEventListener("touchend", onLeave, { passive: true });
    });

    if (k < 1) requestAnimationFrame(frame);
  }
  requestAnimationFrame(frame);
}

export function makePie(cardEl, items, chartRegistry, root) {
  chartRegistry.set(cardEl.id, {
    type: "pie",
    data: JSON.parse(JSON.stringify(items)),
  });

  const chart = cardEl.querySelector(".chart");
  let svg = chart.querySelector("svg");
  let tooltip = chart.querySelector(".tooltip");

  if (!svg || svg.querySelectorAll("*").length === 0 || !svg.querySelector("#slices")) {
    chart.innerHTML = `
      <div class="pie-canvas">
        <svg viewBox="-112 -112 224 224" preserveAspectRatio="xMidYMid meet">
          <circle class="ring" r="80"></circle>
          <g id="slices"></g>
          <circle r="46" fill="#fff" stroke="#e6eaf2" stroke-width="1.5"></circle>
          <text id="sumLabel" text-anchor="middle" dy="4.5" font-size="16" fill="#4a5366" style="font-weight:600"></text>
        </svg>
      </div>
      <div class="tooltip" aria-hidden="true"></div>
    `;

    svg = chart.querySelector("svg");
    tooltip = chart.querySelector(".tooltip");
  }

  const slicesG = svg.querySelector("#slices");
  const sumLabel = svg.querySelector("#sumLabel");
  let legend = cardEl.querySelector(".legend") || cardEl.querySelector(".pie-legend");
  if (!legend) {
    legend = document.createElement("div");
    legend.className = "legend";
    cardEl.appendChild(legend);
  }

  const radius = 80;
  const total = items.reduce((s, d) => s + d.value, 0) || 1;
  sumLabel.textContent = total;

  const cs = getComputedStyle(document.documentElement);
  const baseHueRaw = cs.getPropertyValue("--baseHue") || "220";
  const baseHue = +baseHueRaw.trim();

  items.forEach((d, i) => {
    const hue = (baseHue + i * 60) % 360;
    const isAccent = !!d.accent;
    const s = isAccent ? 80 : 65;
    const l = isAccent ? 50 : 58;

    d.color = `hsl(${hue} ${s}% ${l}%)`;
    d.colorHover = `hsl(${hue} ${Math.min(95, s + 10)}% ${Math.min(70, l + 5)}%)`;
  });

  const polar = (r, a) => [r * Math.cos(a), r * Math.sin(a)];
  function sectorPath(start, end, R) {
    const largeArc = end - start > Math.PI ? 1 : 0;
    const [x1, y1] = polar(R, start);
    const [x2, y2] = polar(R, end);
    return `M 0 0 L ${x1} ${y1} A ${R} ${R} 0 ${largeArc} 1 ${x2} ${y2} Z`;
  }

  legend.innerHTML = "";
  items.forEach((d) => {
    const row = document.createElement("div");
    row.className = "legend-item";
    const sw = document.createElement("div");
    sw.className = "swatch";
    sw.style.background = d.color;
    const title = document.createElement("div");
    title.className = "legend-title";
    title.textContent = d.label + (d.accent ? " • Акцент" : "");
    const pct = Math.round((d.value / total) * 100);
    const val = document.createElement("div");
    val.className = "legend-val";
    val.textContent = `${d.value} (${pct}%)`;
    row.appendChild(sw);
    row.appendChild(title);
    row.appendChild(val);
    legend.appendChild(row);
  });

  slicesG.innerHTML = "";
  let startAngle = -Math.PI / 2;
  const slices = [];
  items.forEach((d) => {
    const groupEl = createSVGElement("g");
    groupEl.setAttribute("class", "slice-group");
    const shape = createSVGElement("path");
    shape.setAttribute("class", "slice" + (d.accent ? " accent" : ""));
    shape.setAttribute("fill", d.color);
    const hit = createSVGElement("path");

    const sweep = (d.value / total) * Math.PI * 2;
    const endAngle = startAngle + sweep;
    const gap = (1.0 * Math.PI) / 180;
    const cut = Math.min(gap / 2, Math.max(0, sweep - 0.003) / 2);
    const slicedStart = startAngle + cut;
    const slicedEnd = endAngle - cut;
    const baseR = radius;
    const R = d.accent ? baseR + 6 : baseR;

    const dPath = sectorPath(slicedStart, slicedEnd, R);
    shape.setAttribute("d", dPath);
    hit.setAttribute("d", dPath);
    hit.setAttribute("fill", "rgba(0,0,0,0)");
    hit.setAttribute("pointer-events", "all");

    groupEl.appendChild(shape);
    groupEl.appendChild(hit);
    slicesG.appendChild(groupEl);

    slices.push({ groupEl, shape, hit, d, R });
    startAngle = endAngle;
  });

  function showTip(evt, d) {
    const pct = Math.round((d.value / total) * 100);
    tooltip.innerHTML = `<strong>${d.label}</strong> — ${d.value} (${pct}%)`;
    tooltip.classList.add("show");
    tooltip.setAttribute("aria-hidden", "false");
    positionTooltip(tooltip, chart, evt.clientX, evt.clientY, "right", 12);
  }
  function hideTip() {
    tooltip.classList.remove("show");
    tooltip.setAttribute("aria-hidden", "true");
  }
  slices.forEach((s) => {
    const scaleHover = s.d.accent ? 1.08 : 1.04;
    const onEnter = (e) => {
      s.groupEl.style.transform = `scale(${scaleHover})`;
      if (!s.d.accent && s.d.colorHover)
        s.shape.setAttribute("fill", s.d.colorHover);
      showTip(e, s.d);
    };
    const onMove = (e) => showTip(e, s.d);
    const onLeave = () => {
      s.groupEl.style.transform = "scale(1)";
      s.shape.setAttribute("fill", s.d.color);
      hideTip();
    };
    s.hit.addEventListener("pointerenter", onEnter);
    s.hit.addEventListener("pointermove", onMove);
    s.hit.addEventListener("pointerleave", onLeave);
    s.hit.addEventListener("touchstart", onEnter, { passive: true });
    s.hit.addEventListener("touchmove", onMove, { passive: true });
    s.hit.addEventListener("touchend", onLeave, { passive: true });
  });
}

export function makeLine(cardEl, rows, chartRegistry, root) {
  chartRegistry.set(cardEl.id, {
    type: "line",
    data: rows.map((r) => ({
      t: r.t instanceof Date ? r.t.toISOString() : r.t,
      y: r.y,
    })),
  });

  const chart = cardEl.querySelector(".chart");
  const svg = chart.querySelector("svg");
  const tooltip = chart.querySelector(".tooltip");

  const WIDTH = 960;
  const HEIGHT = 460;
  svg.setAttribute("viewBox", `0 0 ${WIDTH} ${HEIGHT}`);
  svg.setAttribute("preserveAspectRatio", "xMidYMid meet");

  const M = { top: 18, right: 24, bottom: 56, left: 64 };
  const innerW = WIDTH - M.left - M.right;
  const innerH = HEIGHT - M.top - M.bottom;

  while (svg.firstChild) svg.removeChild(svg.firstChild);

  const g = createSVGGroup(svg, `translate(${M.left},${M.top})`, null, root);
  const gridG = createSVGGroup(g, null, null, root);
  const areaG = createSVGGroup(g, null, null, root);
  const lineG = createSVGGroup(g, null, null, root);
  const ptsG = createSVGGroup(g, null, null, root);
  const labelsG = createSVGGroup(g, null, null, root);
  const axisXG = createSVGGroup(g, null, null, root);
  const axisYG = createSVGGroup(g, null, null, root);
  const hoverG = createSVGGroup(g, null, null, root);

  const data = rows
    .map((d) => ({
      t: d.t instanceof Date ? d.t : new Date(d.t),
      y: +d.y,
    }))
    .sort((a, b) => a.t - b.t);

  if (!data.length) {
    for (let y = 0; y <= 100; y += 20) {
      const yy = innerH * (1 - y / 100);
      createSVGLine(gridG, 0, yy, innerW, yy, "grid-line", root);
      const tick = createSVGGroup(axisYG, null, "tick", root);
      createSVGText(tick, fmtNum(y), -10, yy + 4, { "text-anchor": "end" }, root);
    }
    createSVGLine(axisXG, 0, innerH, innerW, innerH, "axis-line", root);
    return;
  }

  const dates = data.map((d) => d.t);

  const minY = Math.min(...data.map((d) => d.y));
  const maxY = Math.max(...data.map((d) => d.y));
  const spanY = Math.max(1, maxY - minY);
  const stepY = niceStep(spanY, 6);
  const yMin = Math.floor(minY / stepY) * stepY;
  const yMax = Math.ceil(maxY / stepY) * stepY;

  const yScale = (val) =>
    innerH - ((val - yMin) / Math.max(1, yMax - yMin)) * innerH;

  const n = data.length;
  const stepX = n > 1 ? innerW / (n - 1) : 0;
  const xAt = (i) => (n > 1 ? i * stepX : innerW / 2);

  for (let y = yMin; y <= yMax + 1e-9; y += stepY) {
    const yy = yScale(y);
    createSVGLine(gridG, 0, yy, innerW, yy, "grid-line", root);
    const tick = createSVGGroup(axisYG, null, "tick", root);
    createSVGText(tick, fmtNum(y), -10, yy + 4, { "text-anchor": "end" }, root);
  }

  const fmtX = dateFormatter(dates);
  const maxXTicks = Math.min(10, Math.max(3, Math.floor(innerW / 80)));
  const stepIdx = Math.ceil(n / maxXTicks);

  data.forEach((d, i) => {
    if (i % stepIdx === 0 || i === n - 1) {
      const tx = xAt(i);
      const tick = createSVGGroup(axisXG, null, "tick", root);
      createSVGText(tick, fmtX(d.t), tx, innerH + 20, { "text-anchor": "middle" }, root);
    }
  });
  createSVGLine(axisXG, 0, innerH, innerW, innerH, "axis-line", root);

  const pts = data.map((d, i) => ({
    x: xAt(i),
    y: yScale(d.y),
    t: d.t,
    v: d.y,
  }));

  const dLine = pathCatmullRom(pts, 0.18);

  if (!dLine || !dLine.startsWith("M")) {
    return;
  }

  const dArea =
    dLine + ` L ${pts[pts.length - 1].x} ${innerH} L ${pts[0].x} ${innerH} Z`;

  const area = createSVGElement("path");
  area.setAttribute("class", "area");
  area.setAttribute("d", dArea);
  areaG.appendChild(area);

  const path = createSVGElement("path");
  path.setAttribute("class", "line");
  path.setAttribute("d", dLine);
  lineG.appendChild(path);

  const length = path.getTotalLength();
  path.style.strokeDasharray = length;
  path.style.strokeDashoffset = length;
  area.style.opacity = "0";
  requestAnimationFrame(() => {
    path.style.transition = "stroke-dashoffset 900ms cubic-bezier(.2,.8,.2,1)";
    area.style.transition = "opacity 900ms ease";
    path.style.strokeDashoffset = "0";
    area.style.opacity = ".6";
  });

  const pointEls = pts.map((p) => {
    const c = createSVGElement("circle");
    c.setAttribute("class", "point");
    c.setAttribute("cx", p.x);
    c.setAttribute("cy", p.y);
    ptsG.appendChild(c);
    return c;
  });

  const labelEls = pts.map((p) => {
    const lbl = createSVGElement("text");
    lbl.setAttribute("class", "data-label");
    lbl.setAttribute("x", p.x);
    lbl.setAttribute("y", Math.max(12, p.y - 10));
    lbl.textContent = fmtNum(p.v);
    labelsG.appendChild(lbl);
    return lbl;
  });

  const vline = createSVGElement("line");
  vline.setAttribute("class", "hover-line");
  vline.setAttribute("y1", 0);
  vline.setAttribute("y2", innerH);
  hoverG.appendChild(vline);

  const hit = createSVGElement("rect");
  hit.setAttribute("x", 0);
  hit.setAttribute("y", 0);
  hit.setAttribute("width", innerW);
  hit.setAttribute("height", innerH);
  hit.setAttribute("fill", "rgba(0,0,0,0)");
  hoverG.appendChild(hit);

  let activeIdx = -1;

  function onEnter(e) {
    vline.classList.add("show");
    onMove(e);
  }

  function onLeave() {
    vline.classList.remove("show");
    tooltip.classList.remove("show");
    tooltip.setAttribute("aria-hidden", "true");
    if (activeIdx >= 0) {
      pointEls[activeIdx].classList.remove("hover");
      labelEls[activeIdx].classList.remove("hover");
    }
    activeIdx = -1;
  }

  function onMove(e) {
    const pt = getRelative(e);
    const x = clamp(pt.x, 0, innerW);

    let idx = 0;
    let best = Infinity;
    for (let i = 0; i < pts.length; i++) {
      const dx = Math.abs(pts[i].x - x);
      if (dx < best) {
        best = dx;
        idx = i;
      }
    }

    const p = pts[idx];
    vline.setAttribute("x1", p.x);
    vline.setAttribute("x2", p.x);

    if (activeIdx !== idx) {
      if (activeIdx >= 0) {
        pointEls[activeIdx].classList.remove("hover");
        labelEls[activeIdx].classList.remove("hover");
      }
      pointEls[idx].classList.add("hover");
      labelEls[idx].classList.add("hover");
      activeIdx = idx;
    }

    tooltip.innerHTML = `<strong>${new Intl.DateTimeFormat("uz-UZ", {
      dateStyle: "medium",
    }).format(p.t)}</strong><br>Baho: ${fmtNum(p.v)}`;
    tooltip.classList.add("show");
    tooltip.setAttribute("aria-hidden", "false");
    positionTooltip(tooltip, chart, e.clientX, e.clientY, "right", 12);
  }

  function getRelative(evt) {
    const pt = svg.createSVGPoint();
    pt.x = evt.clientX;
    pt.y = evt.clientY;
    const m = svg.getScreenCTM().inverse();
    const p = pt.matrixTransform(m);
    return { x: p.x - M.left, y: p.y - M.top };
  }

  hit.addEventListener("pointerenter", onEnter);
  hit.addEventListener("pointerleave", onLeave);
  hit.addEventListener("pointermove", onMove);
  hit.addEventListener("touchstart", onEnter, { passive: true });
  hit.addEventListener("touchmove", onMove, { passive: true });
  hit.addEventListener("touchend", onLeave, { passive: true });
}
